import React, { useEffect, useRef } from 'react';
import SEO from '../components/SEO';
import {
    ledStorefront,
    letteringNeon,
    corporateBranding,
    vehicleWrapping,
    exhibitionBooth,
    outdoorBillboard,
    wayfindingSystem,
    safetyCompliance,
    restaurantCafe,
    boardRoom,
    gmcBranding,
    marieClaire,
    sehaSalma
} from '../assets/works';

const projectsData = [
    {
        id: 1,
        title: 'LED STOREFRONT SIGNAGE',
        category: 'Retail Branding',
        description: 'Eye-catching LED storefront signage for retail spaces, designed to attract customers and enhance brand visibility.',
        image: ledStorefront
    },
    {
        id: 2,
        title: '3D LETTERING & NEON SIGNS',
        category: 'Custom Fabrication',
        description: 'Premium 3D lettering and vibrant neon signs that create stunning visual impact for businesses.',
        image: letteringNeon
    },
    {
        id: 3,
        title: 'CORPORATE OFFICE BRANDING',
        category: 'Interior Signage',
        description: 'Professional interior signage solutions for corporate offices including reception areas and wayfinding.',
        image: corporateBranding
    },
    {
        id: 4,
        title: 'VEHICLE BRANDING & WRAPPING',
        category: 'Mobile Advertising',
        description: 'Transform your vehicles into powerful moving advertisements with high-quality vinyl wraps.',
        image: vehicleWrapping
    },
    {
        id: 5,
        title: 'EXHIBITION & EVENT SIGNAGE',
        category: 'Event Branding',
        description: 'Custom exhibition booths and event signage that make your brand stand out at trade shows.',
        image: exhibitionBooth
    },
    {
        id: 6,
        title: 'OUTDOOR BILLBOARD SIGNAGE',
        category: 'Large Format Display',
        description: 'Large-scale outdoor billboards and signage designed for maximum visibility and impact.',
        image: outdoorBillboard
    },
    {
        id: 7,
        title: 'WAYFINDING SYSTEMS',
        category: 'Directional Signage',
        description: 'Comprehensive wayfinding solutions for commercial complexes, hospitals, and public spaces.',
        image: wayfindingSystem
    },
    {
        id: 8,
        title: 'SAFETY & COMPLIANCE SIGNAGE',
        category: 'Safety Solutions',
        description: 'Essential safety signage meeting all regulatory requirements for workplace safety.',
        image: safetyCompliance
    },
    {
        id: 9,
        title: 'RESTAURANT & CAFE BRANDING',
        category: 'Hospitality',
        description: 'Complete branding solutions for restaurants including menu boards, wall graphics, and exterior signage.',
        image: restaurantCafe
    },
    {
        id: 10,
        title: 'BOARD ROOM BRANDING',
        category: 'Interior Signage',
        description: 'Professional board room branding and aesthetic enhancements for corporate environments.',
        image: boardRoom
    },
    {
        id: 11,
        title: 'GMC VEHICLE BRANDING',
        category: 'Vehicle Branding',
        description: 'Complete vehicle branding solutions to turn your corporate fleet into moving advertisements.',
        image: gmcBranding
    },
    {
        id: 12,
        title: 'MARIE CLAIRE CAR BRANDING',
        category: 'Vehicle Branding',
        description: 'High-quality promotional vehicle wraps and graphics for marketing campaigns.',
        image: marieClaire
    },
    {
        id: 13,
        title: 'SEHA SALMA REHABILITATION',
        category: 'Corporate Branding',
        description: 'Customized signage and corporate branding for healthcare and rehabilitation centers.',
        image: sehaSalma
    }
];

function ProjectsPage() {
    const projectRefs = useRef([]);
    const headerRef = useRef(null);

    useEffect(() => {
        const observerOptions = {
            threshold: 0.1,
            rootMargin: '0px 0px -50px 0px'
        };

        const projectObserver = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    entry.target.style.opacity = '1';
                    entry.target.style.transform = 'translateY(0)';
                }
            });
        }, observerOptions);

        projectRefs.current.forEach(ref => {
            if (ref) projectObserver.observe(ref);
        });

        if (headerRef.current) {
            headerRef.current.style.opacity = '1';
            headerRef.current.style.transform = 'translateY(0)';
        }

        return () => projectObserver.disconnect();
    }, []);

    return (
        <div className="projects-page">
            <SEO
                title="Our Projects | Signage & Branding Portfolio in Dubai, UAE"
                description="View our portfolio of signage and branding projects in Dubai, UAE. LED storefront signage, 3D lettering, neon signs, vehicle wrapping, corporate branding, exhibition booths, wayfinding systems, and more. Rainbow Craft Signs & Graphics LLC."
                keywords="signage projects dubai, LED signage projects dubai, neon sign projects dubai, 3D lettering projects dubai, vehicle wrapping projects dubai, corporate branding projects dubai, exhibition booth projects dubai, wayfinding projects dubai, signage portfolio dubai, branding portfolio dubai"
                url="/projects"
                schema={{
                    "@context": "https://schema.org",
                    "@type": "CollectionPage",
                    "name": "Our Projects - Rainbow Craft Signs & Graphics",
                    "description": "Portfolio of signage and branding projects in Dubai, UAE.",
                    "url": "https://www.rainbowcraft.ae/projects",
                    "mainEntity": {
                        "@type": "ItemList",
                        "itemListElement": projectsData.map((project, index) => ({
                            "@type": "ListItem",
                            "position": index + 1,
                            "item": {
                                "@type": "CreativeWork",
                                "name": project.title,
                                "description": project.description,
                                "image": typeof project.image === 'string' && project.image.startsWith('http')
                                    ? project.image
                                    : `https://www.rainbowcraft.ae${typeof project.image === 'string' && project.image.startsWith('/') ? '' : '/'}${project.image}`,
                                "url": `https://www.rainbowcraft.ae/projects#${project.id}`,
                                "creator": {
                                    "@id": "https://www.rainbowcraft.ae"
                                }
                            }
                        }))
                    }
                }}
            />
            <div className="page-header" ref={headerRef} style={{
                opacity: 0,
                transform: 'translateY(30px)',
                transition: 'opacity 0.8s ease, transform 0.8s ease'
            }}>
                <div className="container">
                    <h1 className="page-title">OUR PROJECTS</h1>
                    <p className="page-subtitle">
                        Explore our portfolio of creative signage and branding solutions that have helped
                        businesses across the UAE enhance their brand visibility and create lasting impressions.
                    </p>
                </div>
            </div>

            <section className="projects-section">
                <div className="container">
                    <div className="projects-grid">
                        {projectsData.map((project, index) => (
                            <div
                                key={project.id}
                                className="project-item"
                                ref={el => projectRefs.current[index] = el}
                                style={{
                                    opacity: 0,
                                    transform: 'translateY(50px)',
                                    transition: `opacity 0.6s ease ${index * 0.1}s, transform 0.6s ease ${index * 0.1}s`
                                }}
                            >
                                <img src={project.image} alt={`${project.title} - ${project.category} by Rainbow Craft Signs & Graphics Dubai`} />
                                <div className="project-info">
                                    <h3>{project.title}</h3>
                                    <p className="project-category">{project.category}</p>
                                    <p className="project-description">{project.description}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
        </div>
    );
}

export default ProjectsPage;
